/**
 * Reports API
 *
 * Backend endpoints:
 *   GET /order/api/employee/reports/diets/filters
 *   GET /order/api/employee/reports/diets
 *   GET /order/api/employee/reports/diets/export
 *   GET /order/api/employee/reports/meals/filters
 *   GET /order/api/employee/reports/meals
 *   GET /order/api/employee/reports/meals/export
 */
const groupPrefix: string = 'order/api/employee/reports'

type IReportMealsApiResponse = IApiCommonModel<IReportMeal[]> & {
	generated_at?: string
	filters?: IReportMealsResponseFilters
	total_quantity?: number
}

type IReportDietsApiResponse = IApiCommonModel<IReportDietsData> & {
	generated_at?: string
	filters?: IReportDietsResponseFilters
}

const buildPaginationParts = (pagination: { page: number; perPage: number }): string[] => [
	`page[number]=${pagination.page}`,
	`page[size]=${pagination.perPage}`,
]

const buildArrayFilterParts = (key: string, values: (string | number)[], encode: boolean): string[] =>
	values.map((v, i) => `filter[${key}][${i}]=${encode ? encodeURIComponent(v) : v}`)

const buildDateRangeParts = (params: IReportDietsQueryParams | IReportMealsQueryParams): string[] => {
	const parts: string[] = []
	if (params.dateFrom) parts.push(`filter[date_range][from]=${encodeURIComponent(params.dateFrom)}`)
	if (params.dateTo) parts.push(`filter[date_range][to]=${encodeURIComponent(params.dateTo)}`)
	return parts
}

const buildDietsOnlyFilterParts = (params: IReportDietsQueryParams): string[] => {
	const parts: string[] = []
	if (params.offers?.length) parts.push(...buildArrayFilterParts('offers', params.offers, true))
	return parts
}

const buildMealsOnlyFilterParts = (params: IReportMealsQueryParams): string[] => {
	const parts: string[] = []
	if (params.dietGroups?.length) parts.push(...buildArrayFilterParts('diet_groups', params.dietGroups, true))
	if (params.dietIds?.length) parts.push(...buildArrayFilterParts('diet_ids', params.dietIds, false))
	if (params.mealTypeIds?.length) parts.push(...buildArrayFilterParts('meal_type_ids', params.mealTypeIds, false))
	if (params.kcal?.length) parts.push(...buildArrayFilterParts('kcal', params.kcal, false))
	return parts
}

const buildFilterParts = (params: IReportDietsQueryParams | IReportMealsQueryParams): string[] => {
	const specificParts = 'offers' in params ? buildDietsOnlyFilterParts(params) : buildMealsOnlyFilterParts(params)

	return [...buildDateRangeParts(params), ...specificParts]
}

const buildSortParts = (sort: Record<string, EApiSortType>): string[] => {
	const sortVal = Object.entries(sort)
		.map(([key, value]) => (value === EApiSortType.DESC ? `-${key}` : key))
		.join(',')
	return [`sort=${encodeURIComponent(sortVal)}`]
}

const buildReportQueryString = (
	pagination?: { page: number; perPage: number } | null,
	params?: IReportDietsQueryParams | IReportMealsQueryParams,
	sort?: Record<string, EApiSortType>,
): string => {
	const parts: string[] = []

	if (pagination) parts.push(...buildPaginationParts(pagination))
	if (params) parts.push(...buildFilterParts(params))
	if (sort) parts.push(...buildSortParts(sort))

	return parts.join('&')
}

export const apiReports = {
	fetchMealsReport: async (
		page: number = 1,
		perPage: number = 25,
		params?: IReportMealsQueryParams,
		sort?: Record<string, EApiSortType>,
	): Promise<{
		data: IReportMealModel[]
		meta: IApiMetaModel | undefined
		generatedAt: Date | null
		filters: IReportMealsResponseFilters | null
		totalQuantity: number
	}> => {
		const qs = buildReportQueryString({ page, perPage }, params, sort)
		const response = (await apiCommon<IReportMeal[]>(`${groupPrefix}/meals?${qs}`, {
			method: 'GET',
		})) as IReportMealsApiResponse
		return {
			data: response.data?.map(ReportMeal) ?? [],
			meta: response.meta,
			generatedAt: response.generated_at ? new Date(response.generated_at) : null,
			filters: response.filters ?? null,
			totalQuantity: response.total_quantity ?? 0,
		}
	},

	fetchDietsReport: async (
		page: number = 1,
		perPage: number = 25,
		params?: IReportDietsQueryParams,
		sort?: Record<string, EApiSortType>,
	): Promise<{
		data: IReportDietsDataModel
		meta: IApiMetaModel | undefined
		generatedAt: Date | null
		filters: IReportDietsResponseFilters | null
	}> => {
		const qs = buildReportQueryString({ page, perPage }, params, sort)
		const response = (await apiCommon<IReportDietsData>(`${groupPrefix}/diets?${qs}`, {
			method: 'GET',
		})) as IReportDietsApiResponse
		return {
			data: ReportDietsData(
				response.data ??
					({ rows: [], kcal_columns: [], summary: { kcal_totals: {}, grand_total: 0 } } as IReportDietsData),
			),
			meta: response.meta,
			generatedAt: response.generated_at ? new Date(response.generated_at) : null,
			filters: response.filters ?? null,
		}
	},

	fetchMealsFilters: async () => {
		const response = await apiCommonDirect<IReportMealsFiltersResponse>(`${groupPrefix}/meals/filters`, {
			method: 'GET',
		})
		return ReportMealsFilters(response)
	},

	fetchDietsFilters: async () => {
		const response = await apiCommonDirect<IReportDietsFiltersResponse>(`${groupPrefix}/diets/filters`, {
			method: 'GET',
		})
		return ReportDietsFilters(response)
	},

	exportMealsToExcel: async (params?: IReportMealsQueryParams): Promise<void> => {
		const qs = buildReportQueryString(null, params)
		const response = await apiCommonRaw<BlobPart>(`${groupPrefix}/meals/export?${qs}`, {
			method: 'GET',
			responseType: 'blob',
		})

		downloadFileFromResponse(response, `report-meals-${dateToFilterFormat(new Date())}.xlsx`)
	},

	exportDietsToExcel: async (params?: IReportDietsQueryParams): Promise<void> => {
		const qs = buildReportQueryString(null, params)
		const response = await apiCommonRaw<BlobPart>(`${groupPrefix}/diets/export?${qs}`, {
			method: 'GET',
			responseType: 'blob',
		})

		downloadFileFromResponse(response, `report-diets-${dateToFilterFormat(new Date())}.xlsx`)
	},
}
